/**
 * Door - Interactive door entity for level navigation
 * Supports locked/unlocked states, key requirements, and level transitions
 */

import * as THREE from 'three';
import { Entity } from './Entity';
import type { Vector2 } from '../../types/game';
import type { Player } from './Player';
import { playDoorOpen, playDoorClose, playUIError } from '../audio/SoundEffects';
import { inventorySystem } from '../systems/InventorySystem';

export interface DoorConfig {
  id: string;
  position: Vector2;
  size: { width: number; height: number };
  locked: boolean;
  targetLevel?: string | null; // For level-to-level transitions
  targetRoom?: string | null; // For within-level room transitions
  targetSpawn?: string | null;
  requiredKey?: string | null;
  doorStyle?: string; // Visual style: 'security' | 'manager' | undefined (default)
}

export class Door extends Entity {
  // Door properties
  public doorId: string;
  public isLocked: boolean;
  public requiredKey: string | null;
  public doorStyle: string | null;
  public targetLevel: string | null;
  public targetRoom: string | null;
  public targetSpawn: string | null;

  // Interaction
  private interactionRadius: number = 60; // pixels
  private isPlayerNearby: boolean = false;
  private isOpen: boolean = false;

  // Animation state
  private openProgress: number = 0; // 0 = closed, 1 = open
  private openSpeed: number = 2; // speed of opening animation

  // Visual rendering
  private mesh: THREE.Group | null = null;
  private doorMesh: THREE.Mesh | null = null;
  private frameMesh: THREE.Mesh | null = null;

  constructor(config: DoorConfig) {
    super(config.id, 'door', config.position, config.size);

    this.doorId = config.id;
    this.isLocked = config.locked;
    this.requiredKey = config.requiredKey || null;
    this.doorStyle = config.doorStyle || null;
    this.targetLevel = config.targetLevel || null;
    this.targetRoom = config.targetRoom || null;
    this.targetSpawn = config.targetSpawn || null;

    // Doors don't have health
    this.health = -1;
    this.maxHealth = -1;

    // Create visual mesh
    this.createMesh();
  }

  /**
   * Create Three.js mesh for the door
   */
  private createMesh(): void {
    this.mesh = new THREE.Group();

    const isSecurityDoor = this.doorStyle === 'security';
    const isEmployeesDoor = this.requiredKey === 'employee_key' && !isSecurityDoor;

    // Create door frame (outline) - Color varies by door type
    const frameGeometry = new THREE.BoxGeometry(this.size.width, this.size.height, 1);
    let frameColor: number;
    if (isSecurityDoor) {
      frameColor = 0x1a2535; // Dark steel blue frame
    } else if (isEmployeesDoor) {
      frameColor = 0x333333;
    } else {
      frameColor = 0x222222;
    }
    const frameMaterial = new THREE.MeshBasicMaterial({ color: frameColor });
    this.frameMesh = new THREE.Mesh(frameGeometry, frameMaterial);
    this.mesh.add(this.frameMesh);

    // Create door panel (slides open) - Different colors for door types
    const doorGeometry = new THREE.BoxGeometry(this.size.width - 4, this.size.height - 4, 1);
    let doorColor: number;

    if (isSecurityDoor) {
      // Security door - dark steel blue, reinforced look
      doorColor = this.isLocked ? 0x1e2d40 : 0x2d4860;
    } else if (isEmployeesDoor) {
      // Employees Only doors - gray/metallic look
      doorColor = this.isLocked ? 0x5a4a4a : 0x6a6a6a;
    } else {
      // Regular doors - wooden brown
      doorColor = this.isLocked ? 0x654321 : 0x8B4513;
    }

    const doorMaterial = new THREE.MeshBasicMaterial({ color: doorColor });
    this.doorMesh = new THREE.Mesh(doorGeometry, doorMaterial);
    this.mesh.add(this.doorMesh);

    if (isSecurityDoor) {
      // Yellow hazard stripe (top)
      const stripe1 = new THREE.Mesh(
        new THREE.BoxGeometry(this.size.width - 6, 7, 1),
        new THREE.MeshBasicMaterial({ color: 0xFFCC00 })
      );
      stripe1.position.set(0, this.size.height / 4, 0.1);
      this.mesh.add(stripe1);
      // Second yellow stripe (bottom)
      const stripe2 = new THREE.Mesh(
        new THREE.BoxGeometry(this.size.width - 6, 7, 1),
        new THREE.MeshBasicMaterial({ color: 0xFFCC00 })
      );
      stripe2.position.set(0, -this.size.height / 4, 0.1);
      this.mesh.add(stripe2);
      // Small keypad indicator (center-right)
      const keypad = new THREE.Mesh(
        new THREE.BoxGeometry(6, 10, 1),
        new THREE.MeshBasicMaterial({ color: 0x00aa44 })
      );
      keypad.position.set(this.size.width / 2 - 5, 0, 0.2);
      this.mesh.add(keypad);
    } else if (isEmployeesDoor) {
      // Red stripe for employees-only
      const stripe = new THREE.Mesh(
        new THREE.BoxGeometry(this.size.width - 10, 8, 1),
        new THREE.MeshBasicMaterial({ color: 0xff4444 })
      );
      stripe.position.set(0, this.size.height / 4, 0.1);
      this.mesh.add(stripe);
    }

    // Position the mesh
    this.mesh.position.set(this.position.x, this.position.y, 0);
  }

  /**
   * Update door state
   */
  public update(deltaTime: number): void {
    if (this.destroyed || !this.active) return;

    // Update open/close animation
    this.updateAnimation(deltaTime);

    // Update mesh position and animation
    if (this.mesh) {
      this.mesh.position.set(this.position.x, this.position.y, 0);

      // Slide door open/closed horizontally
      if (this.doorMesh) {
        const slideDistance = (this.size.width - 4) * this.openProgress;
        this.doorMesh.position.x = slideDistance / 2;
      }

      // Update door color based on locked state and player proximity
      if (this.doorMesh) {
        const material = this.doorMesh.material as THREE.MeshBasicMaterial;
        const isSecurityDoor = this.doorStyle === 'security';
        const isEmployeesDoor = this.requiredKey === 'employee_key' && !isSecurityDoor;

        if (isSecurityDoor) {
          if (this.isPlayerNearby) {
            material.color.setHex(this.isLocked ? 0x2e4060 : 0x3d5a7a);
          } else {
            material.color.setHex(this.isLocked ? 0x1e2d40 : 0x2d4860);
          }
        } else if (isEmployeesDoor) {
          if (this.isPlayerNearby) {
            material.color.setHex(this.isLocked ? 0x7a5a5a : 0x8a8a8a);
          } else {
            material.color.setHex(this.isLocked ? 0x5a4a4a : 0x6a6a6a);
          }
        } else {
          if (this.isPlayerNearby) {
            material.color.setHex(this.isLocked ? 0x8B4513 : 0xD2691E);
          } else {
            material.color.setHex(this.isLocked ? 0x654321 : 0x8B4513);
          }
        }
      }
    }
  }

  /**
   * Render door
   */
  public render(): void {
    // Mesh is automatically rendered by Three.js scene
    // This method exists for compatibility with Entity interface
  }

  /**
   * Update door animation
   */
  private updateAnimation(deltaTime: number): void {
    if (this.isOpen && this.openProgress < 1) {
      // Opening animation
      this.openProgress = Math.min(1, this.openProgress + deltaTime * this.openSpeed);
    } else if (!this.isOpen && this.openProgress > 0) {
      // Closing animation
      this.openProgress = Math.max(0, this.openProgress - deltaTime * this.openSpeed);
    }
  }

  /**
   * Check if player can interact with this door
   */
  public canInteract(player: Player): boolean {
    if (this.destroyed || !this.active) {
      return false;
    }

    const distance = this.distanceTo(player);
    return distance <= this.interactionRadius;
  }

  /**
   * Attempt to open the door
   */
  public tryOpen(player: Player): boolean {
    if (!this.canInteract(player)) {
      return false;
    }

    if (this.isLocked) {
      // Check if player has required key
      if (this.requiredKey) {
        const hasKey = inventorySystem.hasItem(this.requiredKey);

        if (!hasKey) {
          console.log(`🔒 Door '${this.doorId}' is locked. Required key: ${this.requiredKey}`);
          playUIError(); // Play error sound for missing key
          return false;
        }

        // Player has the key - unlock the door automatically
        console.log(`🔓 Using ${this.requiredKey} to unlock door '${this.doorId}'`);
        this.unlock(this.requiredKey);
        // Don't remove key from inventory - keys are reusable
      } else {
        // Door is locked but has no specific key requirement (shouldn't happen)
        console.log(`🔒 Door '${this.doorId}' is locked with no key specified`);
        playUIError();
        return false;
      }
    }

    // Open the door
    this.open();
    return true;
  }

  /**
   * Open the door
   */
  public open(): void {
    if (this.isLocked) {
      console.warn(`Cannot open locked door '${this.doorId}'`);
      playUIError(); // Play error sound for locked door
      return;
    }

    this.isOpen = true;
    playDoorOpen(); // Play door open sound
    console.log(`Door '${this.doorId}' opened`);
  }

  /**
   * Close the door
   */
  public close(): void {
    this.isOpen = false;
    playDoorClose(); // Play door close sound
    console.log(`Door '${this.doorId}' closed`);
  }

  /**
   * Unlock the door with a key
   */
  public unlock(keyId?: string): boolean {
    if (!this.isLocked) {
      console.log(`Door '${this.doorId}' is already unlocked`);
      return true;
    }

    // If door requires specific key, check it
    if (this.requiredKey && keyId !== this.requiredKey) {
      console.log(`Wrong key for door '${this.doorId}'. Required: ${this.requiredKey}`);
      return false;
    }

    this.isLocked = false;
    console.log(`Door '${this.doorId}' unlocked`);
    return true;
  }

  /**
   * Lock the door
   */
  public lock(): void {
    this.isLocked = true;
    this.close(); // Close the door when locking
    console.log(`Door '${this.doorId}' locked`);
  }

  /**
   * Check if door leads to another level
   */
  public isLevelTransition(): boolean {
    return this.targetLevel !== null && this.targetSpawn !== null;
  }

  /**
   * Check if door leads to another room (within same level)
   */
  public isRoomTransition(): boolean {
    return this.targetRoom !== null;
  }

  /**
   * Get level transition info
   */
  public getLevelTransitionInfo(): { level: string; spawn: string } | null {
    if (!this.isLevelTransition()) {
      return null;
    }

    return {
      level: this.targetLevel!,
      spawn: this.targetSpawn!,
    };
  }

  /**
   * Get room transition info
   */
  public getRoomTransitionInfo(): { room: string; spawn: string | null } | null {
    if (!this.isRoomTransition()) {
      return null;
    }

    return {
      room: this.targetRoom!,
      spawn: this.targetSpawn || null,
    };
  }

  /**
   * Get transition info (legacy method - returns either level or room)
   */
  public getTransitionInfo(): { type: 'level' | 'room'; level?: string; room?: string; spawn: string | null } | null {
    if (this.isLevelTransition()) {
      return {
        type: 'level',
        level: this.targetLevel!,
        spawn: this.targetSpawn || null,
      };
    } else if (this.isRoomTransition()) {
      return {
        type: 'room',
        room: this.targetRoom!,
        spawn: this.targetSpawn || null,
      };
    }

    return null;
  }

  /**
   * Set player nearby state
   */
  public setPlayerNearby(nearby: boolean): void {
    this.isPlayerNearby = nearby;
  }

  // Getters
  public getIsOpen(): boolean {
    return this.isOpen;
  }

  public getIsLocked(): boolean {
    return this.isLocked;
  }

  public getRequiredKey(): string | null {
    return this.requiredKey;
  }

  public getOpenProgress(): number {
    return this.openProgress;
  }

  public getIsPlayerNearby(): boolean {
    return this.isPlayerNearby;
  }

  public getMesh(): THREE.Group | null {
    return this.mesh;
  }

  /**
   * Clean up resources
   */
  public override destroy(): void {
    if (this.mesh) {
      if (this.doorMesh) {
        this.doorMesh.geometry.dispose();
        (this.doorMesh.material as THREE.Material).dispose();
      }
      if (this.frameMesh) {
        this.frameMesh.geometry.dispose();
        (this.frameMesh.material as THREE.Material).dispose();
      }
      // Parent will remove from scene
    }
    super.destroy();
  }

  /**
   * Get door info for UI/debugging
   */
  public getInfo(): {
    id: string;
    locked: boolean;
    open: boolean;
    requiredKey: string | null;
    isLevelTransition: boolean;
    isRoomTransition: boolean;
  } {
    return {
      id: this.doorId,
      locked: this.isLocked,
      open: this.isOpen,
      requiredKey: this.requiredKey,
      isLevelTransition: this.isLevelTransition(),
      isRoomTransition: this.isRoomTransition(),
    };
  }
}
